
import { useState, useEffect, useRef, useCallback } from 'react';
import { AIPrediction } from '@/services/predictions/types';
import { predictionsService } from '@/services/predictions/predictionsService';
import { isSSR } from '@/lib/isSSR';
import { getPredictionsFromCache } from '@/lib/predictions/cache';

interface UseHydratedPredictionsProps {
  initialPredictions?: AIPrediction[];
}

export function useHydratedPredictions({ initialPredictions = [] }: UseHydratedPredictionsProps = {}) {
  const [predictions, setPredictions] = useState<AIPrediction[]>(initialPredictions);
  const [loading, setLoading] = useState(initialPredictions.length === 0);
  const [error, setError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  const loadPredictions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[useHydratedPredictions] Caricamento previsioni...');
      const data = await predictionsService.fetchLatestPredictions();
      
      if (data.length > 0) {
        console.log('[useHydratedPredictions] Previsioni caricate:', data.length);
        setPredictions(data);
      } else {
        console.log('[useHydratedPredictions] Nessuna previsione, generazione in corso...');
      }
    } catch (err) {
      console.error('[useHydratedPredictions] Errore:', err);
    } finally {
      setLoading(false);
      hasInitialized.current = true;
    }
  }, []);

  const retryLoading = useCallback(() => {
    hasInitialized.current = false;
    loadPredictions();
  }, [loadPredictions]);

  useEffect(() => {
    if (isSSR() || hasInitialized.current) {
      return;
    }

    // Se abbiamo dati iniziali, usali
    if (initialPredictions && initialPredictions.length > 0) {
      console.log('[useHydratedPredictions] Uso previsioni iniziali');
      setPredictions(initialPredictions);
      setLoading(false);
      hasInitialized.current = true;
      return;
    }

    // Controlla cache prima di fare richiesta
    const cachedData = getPredictionsFromCache();
    if (cachedData.gold && cachedData.silver) {
      console.log('[useHydratedPredictions] Uso cache');
      setPredictions([cachedData.gold, cachedData.silver]);
      setLoading(false);
      hasInitialized.current = true;
      return;
    }

    console.log('[useHydratedPredictions] Carico dal database...');
    loadPredictions();
  }, [initialPredictions, loadPredictions]);

  return {
    predictions,
    loading,
    error,
    retryLoading
  };
}
